import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '../lib/cn';

/**
 * Button do Trakd · padrão único pra todo o app.
 *
 * Variants:
 *  - primary   — gradient brand (violet→magenta→fuchsia) com glow violet
 *  - secondary — glass surface (bg-surface/60 backdrop-blur)
 *  - outline   — borda violet + texto violet, fill ao hover
 *  - ghost     — sem fundo, só hover sutil
 *  - destructive — vermelho sutil pra ações de delete
 *  - link      — apenas texto com underline
 */
const buttonVariants = cva(
  [
    'group/btn inline-flex shrink-0 items-center justify-center gap-2',
    'font-medium whitespace-nowrap select-none',
    'rounded-full transition-all duration-200',
    'outline-none focus-visible:ring-2 focus-visible:ring-violet/50',
    'disabled:pointer-events-none disabled:opacity-50',
    'active:translate-y-px',
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-trakd-fill text-on-brand font-semibold',
          'shadow-trakd-glow-subtle hover:shadow-trakd-glow-violet',
          'hover:brightness-110',
        ],
        secondary: [
          'bg-surface/60 text-ink backdrop-blur-xl',
          'border border-border-subtle hover:border-violet/50',
          'hover:bg-surface',
        ],
        outline: [
          'border border-violet/60 text-violet bg-transparent',
          'hover:bg-violet/10 hover:border-violet',
        ],
        ghost: ['bg-transparent text-ink-muted', 'hover:bg-surface-elevated/40 hover:text-ink'],
        destructive: [
          'bg-error/10 text-error border border-error/30',
          'hover:bg-error/20 hover:border-error/50',
        ],
        link: ['text-violet underline-offset-4 hover:underline bg-transparent px-0'],
      },
      size: {
        xs: 'h-7 px-3 text-xs',
        sm: 'h-8 px-3.5 text-xs',
        md: 'h-10 px-5 text-sm',
        lg: 'h-12 px-7 text-sm',
        icon: 'h-10 w-10 px-0',
        'icon-sm': 'h-8 w-8 px-0',
        'icon-lg': 'h-12 w-12 px-0',
      },
      fullWidth: { true: 'w-full' },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
export type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, fullWidth, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size, fullWidth }), className)}
      {...props}
    />
  );
});

export { buttonVariants };
